"use client"

import { useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { Checkbox } from "@/components/ui/checkbox"
import {
  X,
  UserPlus,
  Heart,
  Shield,
  Clock,
  Mail,
  User,
  Wrench,
  Car,
  Zap,
  Home,
  CheckCircle,
  AlertTriangle,
  Calendar,
  Star,
  Award,
  Users,
} from "lucide-react"

interface VolunteerRecruitmentProps {
  onClose: () => void
}

interface VolunteerData {
  personalInfo: {
    firstName: string
    lastName: string
    email: string
    phone: string
    city: string
    age: string
  }
  skills: string[]
  availability: {
    weekdays: boolean
    weekends: boolean
    evenings: boolean
    emergencies: boolean
  }
  experience: string
  motivation: string
  hasLicense: boolean
  hasVehicle: boolean
  agreeToBackground: boolean
  agreeToTerms: boolean
}

const skillOptions = [
  { id: "automotive", label: "תיקוני רכב", icon: Car },
  { id: "electrical", label: "חשמל", icon: Zap },
  { id: "plumbing", label: "אינסטלציה", icon: Home },
  { id: "locksmith", label: "מנעולנות", icon: Shield },
  { id: "general", label: "תיקונים כלליים", icon: Wrench },
  { id: "medical", label: "עזרה ראשונה", icon: Heart },
]

export function VolunteerRecruitment({ onClose }: VolunteerRecruitmentProps) {
  const [step, setStep] = useState<"intro" | "form" | "success">("intro")
  const [volunteerData, setVolunteerData] = useState<VolunteerData>({
    personalInfo: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      city: "",
      age: "",
    },
    skills: [],
    availability: {
      weekdays: false,
      weekends: false,
      evenings: false,
      emergencies: false,
    },
    experience: "",
    motivation: "",
    hasLicense: false,
    hasVehicle: false,
    agreeToBackground: false,
    agreeToTerms: false,
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSkillToggle = (skillId: string) => {
    setVolunteerData((prev) => ({
      ...prev,
      skills: prev.skills.includes(skillId) ? prev.skills.filter((s) => s !== skillId) : [...prev.skills, skillId],
    }))
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 3000))
    setIsSubmitting(false)
    setStep("success")
  }

  const isFormValid = () => {
    const { personalInfo, skills, availability, agreeToBackground, agreeToTerms } = volunteerData
    return (
      personalInfo.firstName &&
      personalInfo.lastName &&
      personalInfo.email &&
      personalInfo.phone &&
      personalInfo.city &&
      skills.length > 0 &&
      (availability.weekdays || availability.weekends || availability.evenings || availability.emergencies) &&
      agreeToBackground &&
      agreeToTerms
    )
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
        {step === "intro" && (
          <>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-serif font-bold">הצטרף כמתנדב</CardTitle>
                <Button variant="ghost" size="sm" onClick={onClose}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">עזור לאחרים בקהילה שלך והיה חלק מהשינוי</p>
            </CardHeader>

            <CardContent className="space-y-4">
              {/* Hero Section */}
              <Card className="bg-accent/10 border-accent/20">
                <CardContent className="p-4 text-center space-y-3">
                  <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto">
                    <Heart className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="font-serif font-bold text-lg">היה גיבור יומיומי</h3>
                  <p className="text-sm text-muted-foreground">עזור לאנשים בזמן הצורך והיה חלק מקהילה שדואגת זה לזה</p>
                </CardContent>
              </Card>

              {/* Benefits */}
              <div className="space-y-3">
                <h3 className="font-semibold">למה להתנדב?</h3>
                <div className="space-y-2">
                  {[
                    { icon: Heart, text: "לעזור לאנשים בשעת מצוקה" },
                    { icon: Star, text: "להיות חלק מקהילה חמה ותומכת" },
                    { icon: Users, text: "לתרום לחברה ולהשפיע" },
                    { icon: Shield, text: "להרגיש טוב עם עצמך" },
                  ].map((benefit, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="p-1.5 bg-accent/20 rounded-full">
                        <benefit.icon className="h-3 w-3 text-accent" />
                      </div>
                      <span className="text-sm">{benefit.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Requirements */}
              <div className="space-y-3">
                <h3 className="font-semibold">דרישות בסיסיות</h3>
                <div className="space-y-2">
                  {["גיל 18 ומעלה", "רישיון נהיגה בתוקף"].map(
                    (requirement, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span className="text-sm">{requirement}</span>
                      </div>
                    ),
                  )}
                </div>
              </div>

              <Button
                onClick={() => window.open('https://join.yedidim1230.com/form/', '_blank')}
                className="w-full"
              >
                התחל הרשמה
              </Button>

              <p className="text-xs text-muted-foreground text-center">
                תועבר לדף ההרשמה הרשמי של ידידים
              </p>
            </CardContent>
          </>
        )}

        {step === "form" && (
          <>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-serif font-bold">טופס הרשמה</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setStep("intro")}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">מלא את הפרטים הבאים כדי להצטרף</p>
            </CardHeader>

            <CardContent className="space-y-4">
              {/* Personal Info */}
              <div className="space-y-3">
                <h3 className="font-semibold flex items-center gap-2">
                  <User className="h-4 w-4" />
                  פרטים אישיים
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium">שם פרטי</label>
                    <Input
                      placeholder="שם פרטי"
                      value={volunteerData.personalInfo.firstName}
                      onChange={(e) =>
                        setVolunteerData((prev) => ({
                          ...prev,
                          personalInfo: { ...prev.personalInfo, firstName: e.target.value },
                        }))
                      }
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">שם משפחה</label>
                    <Input
                      placeholder="שם משפחה"
                      value={volunteerData.personalInfo.lastName}
                      onChange={(e) =>
                        setVolunteerData((prev) => ({
                          ...prev,
                          personalInfo: { ...prev.personalInfo, lastName: e.target.value },
                        }))
                      }
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">אימייל</label>
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={volunteerData.personalInfo.email}
                    onChange={(e) =>
                      setVolunteerData((prev) => ({
                        ...prev,
                        personalInfo: { ...prev.personalInfo, email: e.target.value },
                      }))
                    }
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium">טלפון</label>
                    <Input
                      placeholder="050-1234567"
                      value={volunteerData.personalInfo.phone}
                      onChange={(e) =>
                        setVolunteerData((prev) => ({
                          ...prev,
                          personalInfo: { ...prev.personalInfo, phone: e.target.value },
                        }))
                      }
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">עיר</label>
                    <Input
                      placeholder="תל אביב"
                      value={volunteerData.personalInfo.city}
                      onChange={(e) =>
                        setVolunteerData((prev) => ({
                          ...prev,
                          personalInfo: { ...prev.personalInfo, city: e.target.value },
                        }))
                      }
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Skills */}
              <div className="space-y-3">
                <h3 className="font-semibold flex items-center gap-2">
                  <Wrench className="h-4 w-4" />
                  כישורים ומומחיות
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {skillOptions.map((skill) => {
                    const Icon = skill.icon
                    const isSelected = volunteerData.skills.includes(skill.id)

                    return (
                      <Button
                        key={skill.id}
                        variant={isSelected ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleSkillToggle(skill.id)}
                        className="justify-start gap-2 h-auto py-2"
                      >
                        <Icon className="h-3 w-3" />
                        <span className="text-xs">{skill.label}</span>
                      </Button>
                    )
                  })}
                </div>
              </div>

              <Separator />

              {/* Availability */}
              <div className="space-y-3">
                <h3 className="font-semibold flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  זמינות
                </h3>
                <div className="space-y-2">
                  {[
                    { key: "weekdays", label: "ימי חול (9:00-17:00)" },
                    { key: "weekends", label: "סופי שבוע" },
                    { key: "evenings", label: "ערבים (17:00-22:00)" },
                    { key: "emergencies", label: "קריאות חירום (24/7)" },
                  ].map((option) => (
                    <div key={option.key} className="flex items-center space-x-2">
                      <Checkbox
                        id={option.key}
                        checked={volunteerData.availability[option.key as keyof typeof volunteerData.availability]}
                        onCheckedChange={(checked) =>
                          setVolunteerData((prev) => ({
                            ...prev,
                            availability: {
                              ...prev.availability,
                              [option.key]: checked as boolean,
                            },
                          }))
                        }
                      />
                      <label htmlFor={option.key} className="text-sm">
                        {option.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              {/* Additional Info */}
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium">ניסיון קודם (אופציונלי)</label>
                  <Textarea
                    placeholder="ספר על ניסיון קודם בתחום או בהתנדבות..."
                    value={volunteerData.experience}
                    onChange={(e) => setVolunteerData((prev) => ({ ...prev, experience: e.target.value }))}
                    className="min-h-[60px]"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">מה מניע אותך להתנדב?</label>
                  <Textarea
                    placeholder="ספר מה מניע אותך לעזור לאחרים..."
                    value={volunteerData.motivation}
                    onChange={(e) => setVolunteerData((prev) => ({ ...prev, motivation: e.target.value }))}
                    className="min-h-[60px]"
                  />
                </div>
              </div>

              <Separator />

              {/* Vehicle Info */}
              <div className="space-y-3">
                <h3 className="font-semibold flex items-center gap-2">
                  <Car className="h-4 w-4" />
                  רכב
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="license"
                      checked={volunteerData.hasLicense}
                      onCheckedChange={(checked) =>
                        setVolunteerData((prev) => ({ ...prev, hasLicense: checked as boolean }))
                      }
                    />
                    <label htmlFor="license" className="text-sm">
                      יש לי רישיון נהיגה בתוקף
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="vehicle"
                      checked={volunteerData.hasVehicle}
                      onCheckedChange={(checked) =>
                        setVolunteerData((prev) => ({ ...prev, hasVehicle: checked as boolean }))
                      }
                    />
                    <label htmlFor="vehicle" className="text-sm">
                      יש לי רכב זמין
                    </label>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Agreements */}
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="background"
                    checked={volunteerData.agreeToBackground}
                    onCheckedChange={(checked) =>
                      setVolunteerData((prev) => ({ ...prev, agreeToBackground: checked as boolean }))
                    }
                  />
                  <label htmlFor="background" className="text-xs text-muted-foreground">
                    אני מסכים לבדיקת רקע (חינמית)
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="terms"
                    checked={volunteerData.agreeToTerms}
                    onCheckedChange={(checked) =>
                      setVolunteerData((prev) => ({ ...prev, agreeToTerms: checked as boolean }))
                    }
                  />
                  <label htmlFor="terms" className="text-xs text-muted-foreground">
                    אני מסכים לתנאי השימוש ולמדיניות הפרטיות
                  </label>
                </div>
              </div>

              <Button onClick={handleSubmit} disabled={isSubmitting || !isFormValid()} className="w-full">
                {isSubmitting ? "שולח בקשה..." : "שלח בקשה"}
              </Button>

              <div className="text-xs text-muted-foreground text-center bg-muted p-2 rounded">
                <AlertTriangle className="h-3 w-3 inline ml-1" />
                נחזור אליך תוך 24 שעות עם פרטי ההדרכה הראשונה
              </div>
            </CardContent>
          </>
        )}

        {step === "success" && (
          <>
            <CardHeader className="pb-3 text-center">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <CardTitle className="text-lg font-serif font-bold">ברוך הבא למשפחה!</CardTitle>
              <p className="text-sm text-muted-foreground">בקשתך נשלחה בהצלחה</p>
            </CardHeader>

            <CardContent className="space-y-4 text-center">
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 justify-center mb-2">
                    <UserPlus className="h-5 w-5 text-green-600" />
                    <span className="font-semibold text-green-900">מתנדב חדש</span>
                  </div>
                  <p className="text-sm text-green-800 mb-2">
                    {volunteerData.personalInfo.firstName} {volunteerData.personalInfo.lastName}
                  </p>
                  <div className="flex flex-wrap gap-1 justify-center">
                    {volunteerData.skills.map((skillId) => {
                      const skill = skillOptions.find((s) => s.id === skillId)
                      return skill ? (
                        <Badge key={skillId} variant="secondary" className="text-xs">
                          {skill.label}
                        </Badge>
                      ) : null
                    })}
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-2">
                <h3 className="font-semibold">מה קורה עכשיו?</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-primary" />
                    בדיקת רקע תתבצע תוך 24 שעות
                  </li>
                  <li className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-primary" />
                    תקבל הזמנה להדרכה ראשונית
                  </li>
                  <li className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-primary" />
                    הצטרפות לקבוצת המתנדבים
                  </li>
                </ul>
              </div>

              <Button onClick={onClose} className="w-full">
                סגור
              </Button>

              <p className="text-xs text-muted-foreground">תודה שבחרת לעזור לקהילה! נשמח לראותך בפעולה בקרוב.</p>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  )
}
